import { useState } from "react";
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { moveUserToClasses } from "../../Utilities/moveUserHelper";
import SchoolClassPicker from "../UserManagement/SchoolClassPicker";

const MAX_CLASS_NAMES_SHOWN = 5;

// Flyttar en elev eller lärare till en vald klass. Nuvarande skola är förvald, men klassen
// måste väljas. Raderade elever och lärare återställs av servern i samma anrop.
export default function MoveUserDialog({ user, onClose, onMoved }) {
  const intl = useIntl();

  const currentSchoolId = user.Schools?.[0]?.id ?? user.LicenceSchoolId ?? null;
  const currentClassIds = user.ClassIds ?? [];
  const isStudent = user.Type === "Student";
  const isDeleted = user.IsDeleted;

  const [target, setTarget] = useState({
    schoolId: currentSchoolId,
    classId: null,
  });
  const [isMoving, setIsMoving] = useState(false);

  const currentSchoolIds = user.Schools?.length
    ? user.Schools.map((school) => school.id)
    : [currentSchoolId];

  const schoolChanges = target.schoolId !== currentSchoolId;

  const alreadyThere =
    !user.IsDeleted &&
    currentClassIds.length === 1 &&
    currentClassIds[0] === target.classId;

  const schoolText = user.Schools?.length
    ? user.Schools.map(
        (school) => `${school.name} (${school.fortNoxNumber})`,
      ).join(", ")
    : (user.LicenceOnSchool ?? "");

  const classText = !user.Classes?.length
    ? intl.formatMessage({ id: "NoClass" })
    : user.Classes.length > MAX_CLASS_NAMES_SHOWN
      ? intl.formatMessage(
          { id: "ClassCount" },
          { count: user.Classes.length },
        )
      : user.Classes.join(", ");

  const move = async () => {
    setIsMoving(true);

    const moved = await moveUserToClasses(intl, user.Email, [target.classId]);

    if (moved) {
      onMoved();
      onClose();
    } else {
      setIsMoving(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>
        <FormattedMessage id="MoveUserDialogTitle" values={{ name: user.Name }} />
      </ModalHeader>
      <ModalBody>
        <h6>
          <FormattedMessage id="MoveUserFrom" />
        </h6>
        <p className="mb-3">
          {schoolText}
          {schoolText && <br />}
          {classText}
        </p>

        <h6>
          <FormattedMessage id="MoveUserTo" />
        </h6>
        <SchoolClassPicker
          value={target}
          onChange={setTarget}
          currentSchoolIds={currentSchoolIds}
          currentClassIds={currentClassIds}
        />

        {(schoolChanges || isDeleted || !isStudent) && (
          <Alert color="info" className="mb-0">
            <strong>
              <FormattedMessage id="MoveUserGoodToKnow" />
            </strong>
            <ul className="mb-0">
              {isStudent && schoolChanges && (
                <li>
                  <FormattedMessage id="MoveUserInfoStudent" />
                </li>
              )}
              {isDeleted && (
                <li>
                  <FormattedMessage id="MoveUserInfoDeleted" />
                </li>
              )}
              {!isStudent && (
                <li>
                  <FormattedMessage
                    id="MoveUserInfoTeacher"
                    values={{ count: currentClassIds.length }}
                  />
                </li>
              )}
            </ul>
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          disabled={!target.classId || alreadyThere || isMoving}
          onClick={move}
        >
          <FormattedMessage id="Move" />
        </Button>
        <Button color="secondary" outline onClick={onClose}>
          <FormattedMessage id="Cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
